import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useCallback } from "react"
import { AxiosRequestConfig } from "axios"
import {
  axiosInstance,
  setAuthToken,
  setRefreshToken,
} from "../../../api/axios"
import { AppConfig, LoginResponse, UserResponse } from "./types"
import { useAuthStore } from "@/feature/auth/store/authStore"
import { getLanguage } from "@/i18n/utils"
import { SupportedLanguage } from "@/i18n/constants"
import { useIdentify } from "@/hooks/posthog/useIdentify"
import { useFinancialModelSettingsStore } from "@/feature/financial-model/settings/store/financialModelSettingsStore"
import { useSettingsStore } from "@/feature/settings/store/useOrganizationStore"
import { tryCatch } from "@/lib/utils/try-catch"
import { useAppConfigStore } from "../store/app-config"

type LoginFormValues = {
  username: string
  password: string
}

export const USER_QUERY_KEY = [`user`]

export const login = async (params: LoginFormValues) => {
  const { username, password } = params
  const formData = new FormData()
  formData.append(`username`, username)
  formData.append(`password`, password)
  const response = await axiosInstance.post(`/auth/login`, formData)
  return response.data as LoginResponse
}

export const useLoginMutation = () => {
  const { setSession, setUser } = useAuthStore()
  // const { setWorkspace } = useWorkspaceStore()
  const { setOrganization, setActiveWorkspace } = useSettingsStore()
  const { identifyUser } = useIdentify()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: LoginFormValues) => login(data),
    onSuccess: (data) => {
      const { user, ...session } = data
      const { workspaces } = user
      setUser(user)
      if (workspaces && workspaces[0]) {
        // setWorkspace(workspaces[0])
        setOrganization(user.organizations[0])
        setActiveWorkspace(workspaces[0])
        queryClient.invalidateQueries({ queryKey: [`workspace`] })
      }
      if (user.status !== `rejected`) {
        setSession(session)
        setAuthToken(session.access_token)
        setRefreshToken(session.refresh_token)
        identifyUser(user)
      }
      queryClient.clear()
    },
  })
}

type SignUpParams = {
  email: string
  password: string
  clone_anyway?: boolean
}

type SignUpAxiosParams = AxiosRequestConfig<
  SignUpParams & {
    language: SupportedLanguage
    term_acceptance: Date
    clone_anyway?: boolean
  }
>

export const sign_up = async (params: SignUpParams) => {
  const { email, password, clone_anyway } = params
  const term_acceptance = new Date()
  const language = getLanguage()
  const response = await axiosInstance.post<SignUpAxiosParams>(`/auth/signup`, {
    email,
    password,
    language,
    term_acceptance,
    clone_anyway,
  })
  return response.data as never
}

export const useSignUpMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: SignUpParams) => sign_up(data),
    onSuccess: (_, params) => {
      queryClient.invalidateQueries({ queryKey: [`user`] })
      localStorage.setItem(`sign_up_mail`, params.email)
    },
  })
}
export const get_user = async () => {
  const response = await axiosInstance.get<UserResponse>(`/user/me`)
  useAuthStore.setState((state) => ({
    ...state,
    user: response.data,
  }))
  useSettingsStore.setState((state) => ({
    ...state,
    organization: response.data?.organizations[0],
    activeWorkspace: response.data?.workspaces?.[0],
  }))

  return response.data as UserResponse
}

export const useGetUserQuery = () =>
  useQuery({
    queryKey: USER_QUERY_KEY,
    queryFn: get_user,
  })

export const useLogout = () => {
  const { setSession, setUser } = useAuthStore()
  // const { resetWorkspace } = useWorkspaceStore()
  const { resetAll } = useSettingsStore()

  const { resetFinancialModel } = useFinancialModelSettingsStore()
  const queryClient = useQueryClient()
  const { resetIdentify } = useIdentify()
  const logout = useCallback(() => {
    queryClient.clear()
    setAuthToken(``)
    setSession(null)
    setUser(null)

    resetFinancialModel()
    resetIdentify()
    resetAll()
  }, [
    queryClient,
    setSession,
    setUser,

    resetFinancialModel,
    resetIdentify,
    resetAll,
  ])

  return logout
}

export const resend_verification_email = async (email: string) => {
  const response = await axiosInstance.post(`/auth/send-confirmation-email`, {
    email,
  })
  return response.data as never
}

export const useResendVerificationEmailMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (email: string) => resend_verification_email(email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEY })
    },
  })
}

export const verify_email = async (token: string) => {
  const response = await axiosInstance.get(`/auth/verify-email`, {
    params: { token },
  })
  if (response.status === 200) {
    const user_saved_email = localStorage.getItem(`sign_up_mail`)
    if (user_saved_email) {
      localStorage.removeItem(`sign_up_mail`)
    }
  }
  return response.data as never
}

export const useVerifyQuery = (token: string) =>
  useQuery({
    queryKey: [`verify`],
    queryFn: () => verify_email(token),
  })

export const forgot_password = async (email: string) => {
  const response = await axiosInstance.post(`/auth/forgot-password`, { email })
  return response.data as never
}

export const useForgotPasswordMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (email: string) => forgot_password(email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEY })
    },
  })
}

export const reset_password = async (token: string, password: string) => {
  const response = await axiosInstance.post(`/auth/reset-password`, {
    token,
    password,
  })
  return response.data as never
}

export const useResetPasswordMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { token: string; password: string }) =>
      reset_password(data.token, data.password),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEY })
    },
  })
}

const change_user_password = async ({
  old_password,
  new_password,
}: {
  old_password: string
  new_password: string
}) => {
  const response = await axiosInstance.put(`/auth/password`, {
    old_password,
    password: new_password,
  })
  return response.data as UserResponse
}

export const useChangeUserPasswordMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { old_password: string; new_password: string }) =>
      change_user_password(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEY })
    },
  })
}

const bootstapAppConfig = async () => {
  const [config, error] = await tryCatch(
    axiosInstance.get<AppConfig>(`/app-config`)
  )
  if (error) {
    console.warn(`Failed to load app config:`, error)
    // Don't throw error, just return null to allow app to continue
    return null
  }
  let aboveEbitda = config.data.feature_flags.above_ebitda_modules
  if (import.meta.env.NODE_ENV !== `production` || !aboveEbitda) {
    aboveEbitda = {
      twc: true,
      fixed_assets: true,
      vat: true,
      taxes: true,
      provisions: true,
      other_working_capital: true,
      capital_structure: true,
    }
  }
  useAppConfigStore.setState((state) => ({
    ...state,
    appConfig: {
      ...config.data,
      feature_flags: {
        ...config.data.feature_flags,
        above_ebitda_modules: aboveEbitda,
      },
    },
  }))
  return config.data
}

export const useBootstapAppConfigQuery = () =>
  useQuery({
    queryKey: [`app-config`],
    queryFn: bootstapAppConfig,
    staleTime: Infinity,
    retry: 1, // Only retry once since it's not critical
    retryDelay: 1000,
  })
